import { type SyntheticEvent } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router';
import { Box, CircularProgress, Tab, Tabs, Typography } from '@mui/material';
import { HubOutlined, MemoryOutlined, SlideshowOutlined, VideocamOutlined } from '@mui/icons-material';
import OrganizationContentStack from '@/components/organization-content-stack';
import PageHeaderBar from '@/components/common/page-header-bar';
import { useTranslations } from '@/i18n/use-translations';
import { useFeatureAccess } from '@/hooks/use-feature-access';
import ProcessorAdminPanel from '@/components/processor-admin/processor-admin-panel';
import SingleCamAgentAdminPanel from '@/components/singlecam-agent-admin/singlecam-agent-admin-panel';
import MediaAgentAdminPanel from '@/components/media-agent-admin/media-agent-admin-panel';
import { agentPoolsTabPath, isAgentPoolTab, type AgentPoolTab } from '@/components/agent-pools-admin/agent-pools-tabs';
import { useAgentPoolsLiveSync } from '@/hooks/use-agent-pools-live-sync';

const TAB_META: Array<{
  id: AgentPoolTab;
  icon: typeof MemoryOutlined;
  labelKey: string;
  fallback: string;
}> = [
  { id: 'processor', icon: MemoryOutlined, labelKey: 'admin.agentPools.tabs.processor', fallback: 'Media processor' },
  { id: 'singlecam', icon: VideocamOutlined, labelKey: 'admin.agentPools.tabs.singlecam', fallback: 'Single-cam' },
  { id: 'media-agent', icon: SlideshowOutlined, labelKey: 'admin.agentPools.tabs.mediaAgent', fallback: 'Media agent' },
];

export default function AgentPoolsAdminPage() {
  const t = useTranslations();
  const navigate = useNavigate();
  const { tab: tabParam } = useParams<{ tab?: string }>();
  const { hasAccess: hasSingleCam, isLoading: singleCamFeatureLoading } = useFeatureAccess('singlecam-agent');
  const { hasAccess: hasMediaAgent, isLoading: mediaAgentFeatureLoading } = useFeatureAccess('media-agent-pool');

  useAgentPoolsLiveSync(Boolean(tabParam && isAgentPoolTab(tabParam)));

  if (!tabParam) {
    return <Navigate to={agentPoolsTabPath('processor')} replace />;
  }

  if (!isAgentPoolTab(tabParam)) {
    return <Navigate to={agentPoolsTabPath('processor')} replace />;
  }

  const activeTab = tabParam;

  const onTabChange = (_: SyntheticEvent, value: AgentPoolTab) => {
    navigate(agentPoolsTabPath(value));
  };

  const featureLoading = singleCamFeatureLoading || mediaAgentFeatureLoading;

  return (
    <OrganizationContentStack
      content={
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <PageHeaderBar
            mainContent={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <HubOutlined sx={{ fontSize: 24 }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {t('admin.agentPools.title', undefined, 'Agent pools')}
                </Typography>
              </Box>
            }
          />

          <Tabs
            value={activeTab}
            onChange={onTabChange}
            sx={{ mt: 1, borderBottom: 1, borderColor: 'divider', minHeight: 40 }}
          >
            {TAB_META.map((tab) => {
              const Icon = tab.icon;
              return (
                <Tab
                  key={tab.id}
                  value={tab.id}
                  icon={<Icon sx={{ fontSize: 18 }} />}
                  iconPosition="start"
                  label={t(tab.labelKey, undefined, tab.fallback)}
                  sx={{ minHeight: 40, py: 0.5 }}
                />
              );
            })}
          </Tabs>

          <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', pt: 2 }}>
            {featureLoading && activeTab !== 'processor' ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                <CircularProgress />
              </Box>
            ) : activeTab === 'processor' ? (
              <ProcessorAdminPanel embedded />
            ) : activeTab === 'singlecam' ? (
              <SingleCamAgentAdminPanel embedded hasAccess={hasSingleCam} featureLoading={singleCamFeatureLoading} />
            ) : (
              <MediaAgentAdminPanel embedded hasAccess={hasMediaAgent} featureLoading={mediaAgentFeatureLoading} />
            )}
          </Box>
        </Box>
      }
    />
  );
}
